import { Platform, View } from 'react-native';
import { BaseHoverTooltipProps } from './BaseHoverTooltip.types';
import { useEffect, useMemo, useRef } from 'react';
import { useTooltipContext } from '../tooltip-provider/TooltipContext';

export const BaseHoverTooltip: React.FC<BaseHoverTooltipProps> = ({ children, onHover, disabled, ...props }) => {
  const triggerRef = useRef<View>(null);
  const { openTooltip, closeTooltip } = useTooltipContext();
  // Read at measure-callback time (not schedule time) since measure() is async (setTimeout(0) on
  // web) and can resolve after `disabled` flips true — e.g. a co-located press-tooltip opening.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const handleOpen = () => {
    if (disabledRef.current) return;
    triggerRef.current?.measure((_x, _y, w, h, px, py) => {
      if (disabledRef.current) return;
      openTooltip({ ...props, triggerLayout: { x: px, y: py, width: w, height: h } });
    });
    onHover?.();
  };

  const hoverMethods = useMemo(
    () =>
      Platform.OS === 'web'
        ? {
            onMouseEnter: handleOpen,
            onMouseLeave: closeTooltip,
            // Keyboard-focus equivalents of hover, so the tooltip is also reachable via Tab.
            onFocus: handleOpen,
            onBlur: closeTooltip,
          }
        : {},
    [props, openTooltip, closeTooltip],
  );

  useEffect(() => {
    if (disabled) {
      closeTooltip();
    }
  }, [disabled]);

  useEffect(() => {
    return () => {
      closeTooltip();
    };
  }, []);

  return (
    <View ref={triggerRef} {...hoverMethods}>
      {children}
    </View>
  );
};
